import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

interface Series { name: string; data: { x: string; y: number }[] }

export function TotalsByPeriodChart({ series }: { series: Series[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const instance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    instance.current = echarts.init(ref.current);

    const allDates = Array.from(new Set(series.flatMap((s) => s.data.map((p) => p.x)))).sort();
    const COLORS = ['#0A5BFF', '#FF3D00', '#4B5563', '#7A95FF', '#9CA3AF'];

    instance.current.setOption({
      animation: true,
      grid: { left: 60, right: 24, top: 24, bottom: 36 },
      tooltip: { trigger: 'axis' },
      legend: { top: 0, right: 0, textStyle: { color: '#374151', fontSize: 12 } },
      xAxis: {
        type: 'category',
        data: allDates,
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisLabel: { color: '#6B7280', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#F3F4F6' } },
        axisLabel: {
          color: '#6B7280',
          fontSize: 11,
          formatter: (val: number) => abbreviate(val),
        },
      },
      series: series.map((s, i) => ({
        name: s.name,
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2.5, color: COLORS[i % COLORS.length] },
        itemStyle: { color: COLORS[i % COLORS.length] },
        data: allDates.map((d) => {
          const p = s.data.find((pt) => pt.x === d);
          return p ? p.y : null;
        }),
      })),
    });

    const onResize = () => instance.current?.resize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      instance.current?.dispose();
    };
  }, [series]);

  return <div ref={ref} className="w-full h-72" />;
}

function abbreviate(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(n);
}
